import React, { useEffect, useState } from "react";
import { motion, useReducedMotion } from "motion/react";
import {
  AnimatedExpandIcon,
  Headline,
  Body,
  Button,
  Faq,
  Image,
  IconArrowsRepeat,
  IconClockAlarm,
  IconUser,
  SegmentedControl,
  Tag,
} from "@web-monorepo/mds";

import { logEvent } from "@src/utils/logClient";
import { LpNavigation } from "./LpNavigation";
import { CheckItem, HeroSocialProof, ProductChip, TeacherCard } from "./LpKit";
import { getNextEvidenceIndex, getStridePackagePrice } from "./landingPageState";
import {
  STRIDE_EVIDENCE,
  STRIDE_FAQ,
  STRIDE_FEATURED_QUOTE,
  STRIDE_GLANCE,
  STRIDE_LENGTHS,
  STRIDE_PACKAGES,
  STRIDE_STEPS,
  STRIDE_TEACHERS,
  STRIDE_TESTIMONIALS,
  STRIDE_TUTOR_POINTS,
} from "./strideData";

const CTA_HREF = "https://tutor.classdojo.com/#/single-class";

const STRIDE_GLANCE_ICONS = [IconUser, IconClockAlarm, IconArrowsRepeat] as const;

const RESEARCH_LOGOS = [
  { src: "https://static.classdojo.com/tutor/public/lp/stanford_logo.png", alt: "Stanford University" },
  { src: "https://static.classdojo.com/tutor/public/lp/brown_logo.png", alt: "Brown University" },
  {
    src: "https://static.classdojo.com/tutor/public/lp/university_of_chicago_logo.png",
    alt: "The University of Chicago",
  },
  { src: "https://static.classdojo.com/tutor/public/lp/IES_logo.png", alt: "Institute of Education Sciences" },
] as const;

const ResearchLogoLockup: React.FC = () => (
  <div className="flex flex-wrap items-center gap-x-xl gap-y-sm">
    {RESEARCH_LOGOS.map((logo) => (
      <img key={logo.src} src={logo.src} alt={logo.alt} loading="lazy" className="h-32 w-auto object-contain" />
    ))}
  </div>
);

const PROGRESS_CHART_Y_AXIS_LABELS = [
  ["2.0", 30],
  ["1.5", 76],
  ["1", 121],
  ["0.5", 167],
  ["0", 213],
] as const;

const ResponsiveBody: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className = "" }) => (
  <div className={`text-pretty ${className}`}>
    <Body size="2" className="lg:hidden">
      {children}
    </Body>
    <Body size="3" className="hidden lg:block">
      {children}
    </Body>
  </div>
);

/** Rate-of-progress chart. Colors come from MDS color tokens. */
// The original used React.useId() here, but Gatsby's SSG pass and client hydration
// build different component trees, so the generated ids don't match and React
// discards the server markup. The chart renders once per page, so a fixed id is safe.
const ProgressChart: React.FC = () => <ProgressChartSvg gradientId="stride-progress-chart-gradient" />;

const ProgressChartSvg: React.FC<{ gradientId: string }> = ({ gradientId }) => (
  <svg
    viewBox="0 0 470 250"
    className="block w-full h-auto overflow-visible"
    role="img"
    aria-label="Rate of academic progress over one school year: daily 1-on-1 tutoring progresses about twice as fast as classroom instruction alone, once-a-week tutoring only slightly faster"
  >
    <defs>
      <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="var(--emerald-300)" stopOpacity="0.85" />
        <stop offset="100%" stopColor="var(--emerald-300)" stopOpacity="0.05" />
      </linearGradient>
    </defs>
    {[26, 72, 117, 163].map((y) => (
      <line key={y} x1="52" y1={y} x2="330" y2={y} stroke="var(--border-decorative)" strokeWidth="1" />
    ))}
    <line x1="52" y1="209" x2="330" y2="209" stroke="var(--content-primary)" strokeWidth="2" />
    {PROGRESS_CHART_Y_AXIS_LABELS.map(([label, y]) => (
      <text key={label} x="44" y={y} textAnchor="end" fontSize="12" fontWeight="500" fill="var(--content-secondary)">
        {label}
      </text>
    ))}
    <text
      x="20"
      y="126"
      fontSize="11"
      fontWeight="600"
      fill="var(--content-secondary)"
      transform="rotate(-90 20 126)"
      textAnchor="middle"
    >
      Grade levels gained
    </text>
    <text x="52" y="232" fontSize="12" fontWeight="500" fill="var(--content-secondary)">
      Sep
    </text>
    <text x="330" y="232" textAnchor="end" fontSize="12" fontWeight="500" fill="var(--content-secondary)">
      Jun
    </text>
    <polygon points="52,209 122,181 191,143 261,90 330,26 330,209" fill={`url(#${gradientId})`} />
    <polyline
      points="52,209 122,190 191,171 261,152 330,133"
      fill="none"
      stroke="var(--content-primary)"
      strokeWidth="2.5"
      strokeDasharray="7 7"
      strokeLinecap="round"
      opacity="0.3"
    />
    <polyline
      points="52,209 122,186 191,163 261,140 330,117"
      fill="none"
      stroke="var(--pink-300)"
      strokeWidth="6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <polyline
      points="52,209 122,181 191,143 261,90 330,26"
      fill="none"
      stroke="var(--emerald-900)"
      strokeWidth="7"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <circle cx="330" cy="117" r="8" fill="var(--pink-300)" stroke="var(--content-primary)" strokeWidth="2" />
    <circle cx="330" cy="26" r="9" fill="var(--emerald-300)" stroke="var(--content-primary)" strokeWidth="2.5" />
    <text x="346" y="22" fontSize="14" fontWeight="600" fill="var(--emerald-900)">
      Daily 1-on-1
    </text>
    <text x="346" y="39" fontSize="13" fontWeight="500" fill="var(--content-secondary)">
      2× the pace
    </text>
    <text x="346" y="100" fontSize="14" fontWeight="600" fill="var(--pink-700)">
      Once a week
    </text>
    <text x="346" y="117" fontSize="13" fontWeight="500" fill="var(--content-secondary)">
      1.2×
    </text>
    <text x="346" y="138" fontSize="14" fontWeight="500" fill="var(--content-secondary)">
      Classroom only
    </text>
  </svg>
);

export const StridePage: React.FC = () => {
  const [minutes, setMinutes] = useState<"25" | "55">("25");
  const [pick, setPick] = useState(4);
  const [openEvidence, setOpenEvidence] = useState<number | null>(0);
  const prefersReducedMotion = useReducedMotion();

  useEffect(() => {
    logEvent({
      eventName: "web.external.stride.page_view",
      eventValue: window.location.href,
    });
  }, []);

  const handleCtaClick = (placement: string) => () => {
    logEvent({
      eventName: "web.external.stride.cta_click",
      eventValue: window.location.href,
      metadata: { placement, pick, minutes },
    });
  };

  return (
    <div className="brand-tutor bg-paper-100 text-content-primary">
      <LpNavigation product="Stride" getStartedHref={CTA_HREF} />
      {/* Hero */}
      <section className="w-full" data-screen-label="Hero">
        <div className="mds-container grid grid-cols-1 lg:grid-cols-2 gap-2xl items-center py-4xl">
          <div className="flex flex-col items-start gap-y-lg">
            <ProductChip product="Stride" grades="Grades Pre-K – 12" />
            <Headline as="h1" size="3" className="max-w-none lg:max-w-[18ch]">
              Daily tutoring. Twice the learning in a school year.
            </Headline>
            <div className="max-w-none text-balance lg:max-w-[600px]">
              <Body size="1" className="text-content-secondary">
                Daily 1-on-1 sessions with the same tutor add 3 to 15 months of extra learning in a single school year.
              </Body>
            </div>
            <div className="flex flex-col items-start gap-lg md:flex-row md:items-center">
              <Button variant="primary" size="l" as="a" href={CTA_HREF} onClick={handleCtaClick("hero")}>
                Try a class
              </Button>
              <HeroSocialProof />
            </div>
          </div>

          <div className="bg-surface border-2 border-content-primary rounded-lg shadow-button-hard-tutor p-xl flex flex-col gap-y-md">
            <Headline as="h2" size="6">
              2X the progress
            </Headline>
            <ProgressChart />
            <Body size="4" className="text-content-secondary">
              Pace drawn from randomized trials of 1-on-1 tutoring at least 3-5 times per week.
            </Body>
          </div>
        </div>
      </section>

      {/* At a glance */}
      <section className="w-full border-y-2 border-content-primary" data-screen-label="At a glance">
        <div className="mds-container grid grid-cols-1 sm:grid-cols-3 gap-lg py-2xl">
          {STRIDE_GLANCE.map((item, index) => {
            const GlanceIcon = STRIDE_GLANCE_ICONS[index] ?? IconUser;

            return (
              <div key={item.big} className="flex items-center gap-x-sm">
                <span
                  aria-hidden="true"
                  className="shrink-0 size-40 rounded-xs bg-surface border-2 border-content-primary grid place-items-center text-content-primary"
                >
                  <GlanceIcon size="s" />
                </span>
                <Headline as="p" size="6">
                  {item.big}
                </Headline>
              </div>
            );
          })}
        </div>
      </section>

      {/* Research */}
      <section className="w-full bg-emerald-900 dark" data-screen-label="Research" id="research">
        <div className="mds-container grid grid-cols-1 lg:grid-cols-2 gap-2xl py-4xl">
          <div className="flex flex-col gap-y-md items-start">
            <Body size="4" weight="bold" className="uppercase text-brand-300">
              Why frequency matters
            </Body>
            <Headline as="h2" size="3" className="max-w-none lg:max-w-[22ch] text-content-light">
              The research is clear on this one
            </Headline>
            <ResponsiveBody className="max-w-none text-content-light opacity-80 lg:max-w-[46ch]">
              Researchers call it high-dosage tutoring: 1-on-1, three or more times a week, same teacher, one plan. Few
              school interventions show effects this large, and the studies keep pointing to frequency and tutor quality
              as the reasons.
            </ResponsiveBody>
            <div className="hidden lg:block mt-auto pt-lg">
              <ResearchLogoLockup />
            </div>
          </div>

          <div className="flex flex-col gap-y-md">
            {STRIDE_EVIDENCE.map((item, index) => {
              const open = openEvidence === index;
              return (
                <div key={item.stat} className="rounded-md border-1 border-emerald-700 bg-emerald-800 px-lg py-md">
                  <button
                    type="button"
                    aria-expanded={open}
                    onClick={() => setOpenEvidence(getNextEvidenceIndex(!open, index))}
                    className="w-full h-auto p-0 justify-start text-left focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-content-light"
                  >
                    <span className="flex w-full items-center justify-between bg-emerald-800">
                      <Headline as="span" size="6" className="text-brand-300">
                        {item.stat}
                      </Headline>
                      <span className="shrink-0 grid place-items-center w-32 h-32 rounded-full bg-emerald-700 text-content-light">
                        <AnimatedExpandIcon isExpanded={open} />
                      </span>
                    </span>
                  </button>
                  <motion.div
                    initial={false}
                    animate={open ? { height: "auto", opacity: 1 } : { height: 0, opacity: 0 }}
                    transition={prefersReducedMotion ? { duration: 0 } : { duration: 0.4, ease: [0.32, 1, 0.16, 1] }}
                    aria-hidden={!open}
                    className="overflow-hidden will-change-[height,opacity]"
                  >
                    <div className="flex flex-col gap-y-sm pt-sm overflow-hidden">
                      <ResponsiveBody className="text-content-light opacity-85">{item.body}</ResponsiveBody>
                      <Body size="4" className="text-content-light opacity-60">
                        {item.source}
                      </Body>
                    </div>
                  </motion.div>
                </div>
              );
            })}
          </div>
          <div className="lg:hidden">
            <ResearchLogoLockup />
          </div>
        </div>
      </section>

      {/* Mid CTA */}
      <section className="w-full" data-screen-label="Mid CTA">
        <div className="mds-container py-xl">
          <div className="bg-yellow-300 border-2 border-content-primary rounded-lg p-xl flex flex-wrap items-center justify-between gap-lg">
            <div className="flex flex-col gap-y-2xs">
              <Headline as="p" size="5">
                Start the year with support already in place
              </Headline>
              <Body size="2">Don&rsquo;t wait for the first report card. Try a class for $5 and see the fit.</Body>
            </div>
            <Button variant="primary" size="l" as="a" href={CTA_HREF} onClick={handleCtaClick("mid")}>
              Try a class
            </Button>
          </div>
        </div>
      </section>

      {/* Who teaches them */}
      <section className="w-full" data-screen-label="Who teaches them">
        <div className="mds-container grid grid-cols-1 lg:grid-cols-2 gap-2xl items-center py-4xl">
          <div className="aspect-square rounded-lg overflow-hidden border-2 border-content-primary">
            <img
              src="https://static.classdojo.com/tutor/public/lp/tutor_reyes.jpg"
              alt="A certified Dojo tutor"
              loading="lazy"
              className="w-full h-full object-cover"
            />
          </div>
          <div className="flex flex-col gap-y-lg">
            <Headline as="h2" size="3" className="max-w-none lg:max-w-[20ch]">
              One expert tutor who understands your child from day one
            </Headline>
            <div className="flex flex-col gap-y-md max-w-[600px]">
              {STRIDE_TUTOR_POINTS.map((point) => (
                <CheckItem key={point.lead} lead={point.lead} bodySize="2" desktopBodySize="3">
                  {point.body}
                </CheckItem>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Packages */}
      <section className="w-full" data-screen-label="Packages" id="packages">
        <div className="mds-container flex flex-col items-center gap-y-xl py-4xl">
          <Headline as="h2" size="3" className="text-center max-w-[24ch]">
            Pick the pace. We&rsquo;ll hold the slots.
          </Headline>

          <SegmentedControl
            ariaLabel="Session length"
            options={STRIDE_LENGTHS}
            value={minutes}
            onValueChange={setMinutes}
          />

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-lg w-full items-start">
            {STRIDE_PACKAGES.map((option) => {
              const active = option.id === pick;
              return (
                <button
                  type="button"
                  key={option.id}
                  aria-pressed={active}
                  onClick={() => setPick(option.id)}
                  className="block w-full h-auto p-0 text-left focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-border"
                >
                  <span
                    className={`block w-full rounded-lg p-lg bg-surface border-2 ${
                      active ? "border-content-primary shadow-button-hard-tutor" : "border-border-decorative shadow-sm"
                    }`}
                  >
                    <span className="flex items-center justify-between gap-x-sm min-h-32">
                      <Body as="span" size="2" weight="bold">
                        {option.label}
                      </Body>
                      <Tag
                        color={active ? "emerald" : "neutral"}
                        size="xs"
                        className={active ? "bg-brand-300 text-content-primary" : "bg-paper-200 text-content-secondary"}
                      >
                        {option.tag}
                      </Tag>
                    </span>
                    <span className="flex flex-col gap-y-2xs pt-sm">
                      <Body as="span" size="3" weight="medium" className="text-content-secondary">
                        From
                      </Body>
                      <span className="flex items-baseline gap-x-2xs">
                        <Headline as="span" size="2">
                          ${getStridePackagePrice(option.price, minutes)}
                        </Headline>
                        <Body as="span" size="1" weight="medium">
                          /class
                        </Body>
                      </span>
                    </span>
                  </span>
                </button>
              );
            })}
          </div>

          <Button variant="primary" size="l" as="a" href={CTA_HREF} onClick={handleCtaClick("packages")}>
            Try a class
          </Button>

          <Body as="p" size="4" className="text-content-secondary text-center">
            Prices are per class, per child, billed weekly.
          </Body>
        </div>
      </section>

      {/* How it works */}
      <section className="w-full bg-yellow-300 border-y-2 border-content-primary" data-screen-label="How it works">
        <div className="mds-container flex flex-col gap-y-xl py-4xl">
          <Headline as="h2" size="4" className="text-center">
            2 minutes to set up. A whole extra year of growth.
          </Headline>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-lg">
            {STRIDE_STEPS.map((step) => (
              <div
                key={step.title}
                className="bg-paper-100 border-2 border-content-primary rounded-lg p-lg flex flex-col gap-y-sm"
              >
                <img src={step.image} alt="" className="block w-full h-auto" />
                <Body size="4" weight="bold" className="uppercase text-content-secondary">
                  {step.step}
                </Body>
                <Headline as="h3" size="6">
                  {step.title}
                </Headline>
                <ResponsiveBody className="text-content-secondary">{step.body}</ResponsiveBody>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Tutors */}
      <section className="w-full" data-screen-label="Teachers" id="teachers">
        <div className="mds-container flex flex-col items-center gap-y-xl py-4xl">
          <Headline as="h2" size="4" className="text-center">
            Meet our expert daily tutors
          </Headline>
          <div className="max-w-[64ch] text-balance text-center">
            <Body size="1" className="text-content-secondary">
              Every tutor is a certified teacher, most with years in a real classroom, and you choose the one your child
              works with.
            </Body>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-lg w-full">
            {STRIDE_TEACHERS.map((teacher) => (
              <TeacherCard key={teacher.name} teacher={teacher} />
            ))}
          </div>
        </div>
      </section>

      {/* Proof */}
      <section className="w-full overflow-hidden" data-screen-label="Proof">
        <div className="mds-container flex flex-col gap-y-lg py-4xl overflow-visible">
          <Headline as="h2" size="4">
            Better grades, more confidence
          </Headline>
          <Body size="1" className="text-content-secondary">
            Here&rsquo;s what parents and kids have to say about Dojo Tutor.
          </Body>

          <div className="grid grid-cols-1 lg:grid-cols-[340px_1fr] bg-surface border-2 border-content-primary rounded-xl shadow-button-hard-tutor overflow-hidden">
            <Image
              src="https://static.classdojo.com/tutor/public/lp/feature_testionial.jpg"
              alt="A smiling mother holding her son"
              loading="lazy"
              className="w-full h-full min-h-[320px] aspect-square lg:aspect-auto object-cover border-b-2 lg:border-b-0 lg:border-r-2 border-content-primary"
            />
            <div className="p-xl flex flex-col justify-center gap-y-lg">
              <div className="self-start">
                <Tag color="yellow" size="sm">
                  {STRIDE_FEATURED_QUOTE.tag}
                </Tag>
              </div>
              <Headline as="p" size="4">
                {STRIDE_FEATURED_QUOTE.quote}
              </Headline>
              <div className="flex flex-col gap-y-2xs">
                <Body size="2" weight="bold">
                  {STRIDE_FEATURED_QUOTE.name}
                </Body>
                <Body size="3" className="text-content-secondary">
                  {STRIDE_FEATURED_QUOTE.grade}
                </Body>
              </div>
            </div>
          </div>
          <div className="relative left-1/2 mt-xl flex w-screen -translate-x-1/2 snap-x snap-mandatory overflow-x-auto pb-lg pt-xs scroll-smooth [scrollbar-color:var(--content-primary)_var(--ink-200)] [scrollbar-width:auto] md:left-auto md:w-auto md:translate-x-0 [&::-webkit-scrollbar]:h-12 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-content-primary [&::-webkit-scrollbar-track]:mx-lg [&::-webkit-scrollbar-track]:rounded-full [&::-webkit-scrollbar-track]:bg-ink-200 md:[&::-webkit-scrollbar-track]:mx-0">
            <span aria-hidden="true" className="w-lg shrink-0 md:hidden" />
            {STRIDE_TESTIMONIALS.map((item, index) => (
              <div
                key={item.quote}
                className={`flex w-[calc(100vw-51px)] max-w-[400px] shrink-0 snap-start ${
                  index === 0 ? "scroll-ml-lg md:scroll-ml-0" : ""
                } ${index < STRIDE_TESTIMONIALS.length - 1 ? "mr-lg" : "scroll-mr-lg md:scroll-mr-0"}`}
              >
                <div className="w-full bg-surface border-2 border-content-primary rounded-md p-lg flex flex-col gap-y-lg">
                  <img src={item.avatar} alt="" className="shrink-0 size-48 rounded-full object-cover" />
                  <div className="flex-1 text-pretty">
                    <Body size="1" weight="medium">
                      {item.quote}
                    </Body>
                  </div>
                  <div>
                    <Body as="div" size="3" weight="bold">
                      {item.name}
                    </Body>
                    <Tag color="yellow" size="sm" className="mt-sm">
                      {item.grade}
                    </Tag>
                  </div>
                </div>
              </div>
            ))}
            <span aria-hidden="true" className="w-lg shrink-0 md:hidden" />
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="w-full" data-screen-label="FAQ" id="faq">
        <Faq className="py-4xl" headline="Common questions" headlineSize="3" items={STRIDE_FAQ} includeSchema />
      </section>

      {/* Closing CTA */}
      <section className="w-full" data-screen-label="Closing CTA">
        <div className="mds-container pb-4xl">
          <div className="bg-brand-300 border-2 border-content-primary rounded-xl p-2xl flex flex-col items-center gap-y-lg text-center">
            <Headline as="h2" size="3" className="max-w-[24ch]">
              Start with a $5 class this week
            </Headline>
            <Body size="1">Meet a tutor, get a learning plan, then choose the schedule that fits.</Body>
            <Button
              variant="primary"
              context="onAccentLight"
              size="l"
              as="a"
              href={CTA_HREF}
              onClick={handleCtaClick("closing")}
            >
              Try a class for $5
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default StridePage;
